import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { useGame } from "@/context/GameContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || "https://backend-production-cfa1f.up.railway.app"}/api`;
const GOLD = "#F1E194";

// Local asset — downloaded to /public to avoid hotlink issues
const BG_URL = "/tournament-bg.jpg";

const PAGE_STYLE = {
  minHeight: "100vh",
  position: "relative",
};

// Background image div — absolute-fills page, z-index 0
// Using a separate div (not CSS background on container) ensures it works
// even when the .App wrapper has its own background-color
function TourBg() {
  return (
    <>
      {/* Image layer */}
      <div style={{
        position: "fixed",
        inset: 0,
        zIndex: 0,
        backgroundImage: `url('${BG_URL}')`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }} />
      {/* Overlay — burgundy-dark, ~50% opacity so image stays visible */}
      <div style={{
        position: "fixed",
        inset: 0,
        zIndex: 1,
        background: "linear-gradient(180deg, rgba(20,3,8,0.55) 0%, rgba(10,1,4,0.62) 100%)",
      }} />
    </>
  );
}

const COLORS = [
  "#ef4444","#3b82f6","#22c55e","#f59e0b","#a855f7","#ec4899","#14b8a6","#f97316",
  "#64748b","#0ea5e9","#84cc16","#f43f5e","#8b5cf6","#06b6d4","#d97706","#10b981",
  "#dc2626","#1d4ed8","#15803d","#b45309","#7e22ce","#be185d","#0f766e","#c2410c",
  "#475569","#0369a1","#4d7c0f","#92400e","#6d28d9","#9d174d","#134e4a","#9a3412",
];

const SIZES = [
  { count: 4,  matches: 3,  icon: "🥉", label: "4 فرق",   planId: "tournament_4",  desc: "مثالية لسهرة سريعة مع الأصحاب" },
  { count: 8,  matches: 7,  icon: "🥈", label: "8 فرق",   planId: "tournament_8",  desc: "البطولة المثالية للمجموعات الكبيرة" },
  { count: 16, matches: 15, icon: "🥇", label: "16 فريق", planId: "tournament_16", desc: "بطولة ضخمة لمناسبات لا تُنسى" },
  { count: 32, matches: 31, icon: "👑", label: "32 فريق", planId: "tournament_32", desc: "بطولة كأس حُجّة الكبرى" },
];

function generateBracket(teams) {
  const size = Math.pow(2, Math.ceil(Math.log2(teams.length)));
  const padded = [...teams, ...Array(size - teams.length).fill(null)];
  const rounds = [];
  let current = [...padded];
  const roundNames = { 2: "النهائي", 4: "نصف النهائي", 8: "ربع النهائي", 16: "دور الـ 16", 32: "دور الـ 32" };
  while (current.length > 1) {
    const matches = [];
    const nextRound = [];
    for (let i = 0; i < current.length; i += 2) {
      const t1 = current[i]; const t2 = current[i + 1];
      if (t1 && !t2) {
        matches.push({ id: `r${rounds.length}_m${i/2}`, team1: t1, team2: null, winner: t1.id, isBye: true });
        nextRound.push(t1);
      } else if (!t1 && t2) {
        matches.push({ id: `r${rounds.length}_m${i/2}`, team1: null, team2: t2, winner: t2.id, isBye: true });
        nextRound.push(t2);
      } else {
        matches.push({ id: `r${rounds.length}_m${i/2}`, team1: t1, team2: t2, winner: null, isBye: false });
        nextRound.push(null);
      }
    }
    rounds.push({ name: roundNames[current.length] || `جولة ${rounds.length + 1}`, matches });
    current = nextRound;
  }
  return rounds;
}

function TeamAvatar({ color, image, size = 48 }) {
  if (image) {
    return (
      <img src={image} alt="" style={{
        width: size, height: size, borderRadius: "50%",
        objectFit: "cover", border: `2px solid ${color}`,
        boxShadow: `0 0 8px ${color}44`, flexShrink: 0,
      }} />
    );
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%", flexShrink: 0,
      background: `linear-gradient(135deg, ${color}25, ${color}55)`,
      border: `2px solid ${color}55`,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: size * 0.4, color, fontWeight: 900,
    }}>🏆</div>
  );
}

// ── Resume popup ──────────────────────────────────────────────────────────────
function ResumePopup({ tournamentState, onResume, onCreateNew, onBack }) {
  const sizeInfo = tournamentState ? SIZES.find(s => s.count === tournamentState.size) || null : null;
  const completedMatches = tournamentState
    ? tournamentState.rounds?.reduce((a, r) => a + r.matches.filter(m => !!m.winner && !m.isBye).length, 0) || 0 : 0;

  return (
    <div style={{ ...PAGE_STYLE, display: "flex", alignItems: "center", justifyContent: "center", padding: "24px 16px" }} dir="rtl">
      <TourBg />
      <div style={{
        position: "relative", zIndex: 2,
        width: "100%", maxWidth: 420,
        background: "rgba(10,2,4,0.82)",
        border: "1px solid rgba(241,225,148,0.2)",
        borderRadius: 20,
        padding: "36px 28px",
        boxShadow: "0 20px 60px rgba(0,0,0,0.6)",
      }}>
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div style={{ fontSize: "3rem", marginBottom: 12, lineHeight: 1 }}>🏆</div>
          <h2 style={{ fontFamily: "Cairo, sans-serif", fontWeight: 900, fontSize: "1.4rem", color: GOLD, margin: 0, marginBottom: 8 }}>
            لديك بطولة غير مكتملة
          </h2>
          {sizeInfo && (
            <p style={{ color: "rgba(241,225,148,0.5)", fontSize: "0.85rem", fontFamily: "Cairo", margin: 0 }}>
              {sizeInfo.icon} بطولة {sizeInfo.label} · {completedMatches} مباراة مكتملة
            </p>
          )}
          {tournamentState?.teams?.length > 0 && (
            <p style={{ color: "rgba(241,225,148,0.3)", fontSize: "0.78rem", fontFamily: "Cairo", marginTop: 4, margin: "4px 0 0" }}>
              {tournamentState.teams.slice(0, 4).map(t => t.name).join(" · ")}{tournamentState.teams.length > 4 ? " ..." : ""}
            </p>
          )}
        </div>
        <div style={{ height: 1, background: "rgba(241,225,148,0.1)", marginBottom: 20 }} />
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <button onClick={onResume} style={{
            width: "100%", padding: "14px 0", borderRadius: 14,
            background: "linear-gradient(135deg, #5B0E14, #9A1E28)",
            color: GOLD, border: "1.5px solid rgba(241,225,148,0.3)",
            fontFamily: "Cairo, sans-serif", fontWeight: 900, fontSize: "1rem",
            cursor: "pointer",
          }}>
            ▶ استكمال البطولة
          </button>
          <button onClick={onCreateNew} style={{
            width: "100%", padding: "13px 0", borderRadius: 14,
            background: "rgba(255,255,255,0.04)",
            color: "rgba(241,225,148,0.6)", border: "1px solid rgba(241,225,148,0.15)",
            fontFamily: "Cairo, sans-serif", fontWeight: 700, fontSize: "0.95rem",
            cursor: "pointer",
          }}>
            ✨ إنشاء بطولة جديدة
          </button>
          <button onClick={onBack} style={{
            width: "100%", padding: "11px 0", borderRadius: 14,
            background: "transparent",
            color: "rgba(241,225,148,0.35)", border: "1px solid rgba(241,225,148,0.08)",
            fontFamily: "Cairo, sans-serif", fontWeight: 600, fontSize: "0.88rem",
            cursor: "pointer",
          }}>
            رجوع
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Owned selector ─────────────────────────────────────────────────────────────
function OwnedSelector({ ownedSizes, onSelect }) {
  return (
    <div style={{ ...PAGE_STYLE, display: "flex", alignItems: "center", justifyContent: "center", padding: "32px 16px" }} dir="rtl">
      <TourBg />
      <div style={{ position: "relative", zIndex: 2, width: "100%", maxWidth: 520 }}>
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <div style={{ fontSize: "3rem", marginBottom: 10 }}>🏆</div>
          <h1 style={{ fontFamily: "Cairo, sans-serif", fontWeight: 900, fontSize: "1.7rem", color: GOLD, margin: 0, marginBottom: 6 }}>
            بطولاتي المتاحة
          </h1>
          <p style={{ color: "rgba(241,225,148,0.4)", fontSize: "0.88rem", margin: 0 }}>اختر البطولة التي تريد إطلاقها</p>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {ownedSizes.map((s, idx) => (
            <div key={s.planId} onClick={() => onSelect(s)} style={{
              background: "rgba(10,2,4,0.78)",
              border: "1px solid rgba(241,225,148,0.2)",
              borderRadius: 16, padding: "18px 20px",
              display: "flex", alignItems: "center", gap: 16,
              cursor: "pointer", transition: "border-color 0.2s",
            }}
            onMouseOver={e => e.currentTarget.style.borderColor = "rgba(241,225,148,0.45)"}
            onMouseOut={e => e.currentTarget.style.borderColor = "rgba(241,225,148,0.2)"}
            >
              <span style={{ fontSize: "2rem" }}>{s.icon}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: "Cairo", fontWeight: 900, fontSize: "1.1rem", color: GOLD }}>{s.label}</div>
                <div style={{ color: "rgba(241,225,148,0.4)", fontSize: "0.8rem" }}>{s.desc}</div>
              </div>
              <div style={{ color: "rgba(241,225,148,0.5)", fontSize: "0.9rem", fontWeight: 700 }}>ابدأ ←</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── MAIN ───────────────────────────────────────────────────────────────────────
export default function TournamentSetupPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setTournament, setGameMode, currentUser, userToken, setCurrentUser, tournamentState } = useGame();

  const [phase, setPhase] = useState(0); // 0=checking, "resume", 1=selector, 2=setup
  const [ownedSizes, setOwnedSizes] = useState([]);
  const [selectedSize, setSelectedSize] = useState(null);
  const [names, setNames] = useState(Array(32).fill(""));
  const [images, setImages] = useState(Array(32).fill(null));
  const [consuming, setConsuming] = useState(false);
  const fileRefs = useRef(Array(32).fill(null));
  const checkedRef = useRef(false);

  const doCheck = async () => {
    setPhase(0);
    let user = currentUser;
    try {
      if (userToken) {
        const { data } = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${userToken}` } });
        user = data;
        setCurrentUser(data);
        localStorage.setItem("hujjah_user", JSON.stringify(data));
      }
    } catch (_) {}

    const tc = user?.tournament_credits || {};
    const owned = SIZES.filter(s => (tc[s.planId] || 0) > 0);
    const autoSize = location.state?.autoStartSize;

    if (autoSize) {
      const target = SIZES.find(s => s.count === autoSize);
      if (target && (tc[target.planId] || 0) > 0) { setSelectedSize(target); setPhase(2); return; }
    }
    if (owned.length === 0) { navigate("/pricing", { replace: true, state: { highlightTournament: true } }); return; }
    if (owned.length === 1) { setSelectedSize(owned[0]); setPhase(2); return; }
    setOwnedSizes(owned);
    setPhase(1);
  };

  useEffect(() => {
    if (checkedRef.current) return;
    checkedRef.current = true;
    if (tournamentState && !tournamentState.champion) { setPhase("resume"); return; }
    doCheck();
  }, []); // eslint-disable-line

  const handleImageUpload = (idx, file) => {
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) { toast.error("الصورة أكبر من 3 ميغابايت"); return; }
    const reader = new FileReader();
    reader.onload = (e) => { const n = [...images]; n[idx] = e.target.result; setImages(n); };
    reader.readAsDataURL(file);
  };

  const handleStart = async () => {
    if (!selectedSize) return;
    const activeNames = names.slice(0, selectedSize.count);
    if (activeNames.some(n => !n.trim())) { toast.error("أدخل أسماء جميع الفرق!"); return; }
    const unique = new Set(activeNames.map(n => n.trim().toLowerCase()));
    if (unique.size < selectedSize.count) { toast.error("أسماء الفرق يجب أن تكون مختلفة!"); return; }

    setConsuming(true);
    try {
      await axios.post(`${API}/tournament/consume`, { plan_id: selectedSize.planId }, { headers: { Authorization: `Bearer ${userToken}` } });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "لا يوجد رصيد بطولة");
      setConsuming(false);
      return;
    }
    setConsuming(false);
    const teams = activeNames.map((name, i) => ({ id: `t${i}`, name: name.trim(), color: COLORS[i % COLORS.length], image: images[i] || null }));
    const rounds = generateBracket(teams);
    setGameMode("tournament");
    setTournament({ teams, rounds, champion: null, size: selectedSize.count, createdAt: Date.now() });
    navigate("/tournament/bracket");
  };

  if (phase === 0) return (
    <div style={{ ...PAGE_STYLE, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <TourBg />
      <div style={{ position: "relative", zIndex: 2, textAlign: "center" }}>
        <div style={{ fontSize: "3rem", marginBottom: 12 }}>🏆</div>
        <p style={{ color: "rgba(241,225,148,0.5)", fontSize: "0.9rem", fontFamily: "Cairo", fontWeight: 700 }}>جاري التحقق...</p>
      </div>
    </div>
  );

  if (phase === "resume") return <ResumePopup tournamentState={tournamentState} onResume={() => navigate("/tournament/bracket")} onCreateNew={() => doCheck()} onBack={() => navigate(-1)} />;
  if (phase === 1) return <OwnedSelector ownedSizes={ownedSizes} onSelect={(s) => { setSelectedSize(s); setPhase(2); }} />;

  // ── Phase 2: team setup ──────────────────────────────────────────────────────
  return (
    <div style={{ ...PAGE_STYLE, padding: "28px 16px 48px" }} dir="rtl">
      <TourBg />
      <style>{`
        @keyframes teamIn { from { opacity:0; transform:translateX(12px); } to { opacity:1; transform:none; } }
        .t-row { animation: teamIn 0.28s ease-out both; }
        input[type=file] { display:none; }
        .av-wrap:hover .av-ov { opacity:1 !important; }
      `}</style>

      <div style={{ position: "relative", zIndex: 2, maxWidth: 800, margin: "0 auto" }}>
        {/* Back */}
        <button
          onClick={() => ownedSizes.length > 1 ? (setSelectedSize(null), setPhase(1)) : navigate("/mode")}
          style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            background: "rgba(10,2,4,0.7)", border: "1px solid rgba(241,225,148,0.22)",
            borderRadius: 10, padding: "8px 14px", marginBottom: 20,
            color: "rgba(241,225,148,0.7)", fontFamily: "Cairo, sans-serif",
            fontWeight: 700, fontSize: "0.9rem", cursor: "pointer",
          }}
        >
          → {ownedSizes.length > 1 ? "بطولاتي" : "رجوع"}
        </button>

        {/* Main card */}
        <div style={{
          background: "rgba(10,2,4,0.78)",
          border: "1px solid rgba(241,225,148,0.18)",
          borderRadius: 22,
          padding: "32px 28px",
        }}>
          {/* Header */}
          <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 28 }}>
            <span style={{ fontSize: "2.4rem", lineHeight: 1 }}>{selectedSize.icon}</span>
            <div>
              <h2 style={{ fontFamily: "Cairo, sans-serif", fontWeight: 900, fontSize: "1.6rem", color: GOLD, margin: 0 }}>
                بطولة {selectedSize.label}
              </h2>
              <p style={{ color: "rgba(241,225,148,0.4)", fontSize: "0.82rem", margin: 0 }}>
                {selectedSize.matches} مباراة · {selectedSize.count} فريق
              </p>
            </div>
          </div>

          {/* Teams grid */}
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
            gap: 10, marginBottom: 22,
          }}>
            {Array.from({ length: selectedSize.count }).map((_, i) => {
              const filled = names[i].trim().length > 0;
              return (
                <div key={i} className="t-row" style={{
                  animationDelay: `${i * 0.03}s`,
                  display: "flex", alignItems: "center", gap: 12,
                  padding: "12px 14px", borderRadius: 14,
                  background: filled ? `${COLORS[i % COLORS.length]}10` : "rgba(91,14,20,0.18)",
                  border: `1.5px solid ${filled ? COLORS[i % COLORS.length] + "55" : "rgba(241,225,148,0.1)"}`,
                  transition: "border-color 0.2s, background 0.2s",
                }}>
                  {/* Avatar upload */}
                  <div className="av-wrap" style={{ position: "relative", flexShrink: 0, cursor: "pointer" }}
                    onClick={() => fileRefs.current[i]?.click()}>
                    <TeamAvatar color={COLORS[i % COLORS.length]} image={images[i]} size={46} />
                    <div className="av-ov" style={{
                      position: "absolute", inset: 0, borderRadius: "50%",
                      background: "rgba(0,0,0,0.5)", display: "flex",
                      alignItems: "center", justifyContent: "center",
                      opacity: 0, transition: "opacity 0.15s",
                    }}>
                      <span style={{ fontSize: 14, color: "#fff" }}>📷</span>
                    </div>
                    <input type="file" accept="image/*"
                      ref={el => fileRefs.current[i] = el}
                      onChange={e => handleImageUpload(i, e.target.files[0])} />
                  </div>

                  {/* Name input */}
                  <input
                    data-testid={`tournament-team-${i + 1}`}
                    type="text"
                    value={names[i]}
                    onChange={e => { const n = [...names]; n[i] = e.target.value; setNames(n); }}
                    placeholder={`الفريق ${i + 1}`}
                    maxLength={16}
                    style={{
                      flex: 1, background: "transparent",
                      color: GOLD, border: "none", outline: "none",
                      fontFamily: "Cairo, sans-serif", fontWeight: 700,
                      fontSize: "1rem", textAlign: "right",
                      caretColor: COLORS[i % COLORS.length],
                    }}
                  />
                  {filled && <span style={{ color: COLORS[i % COLORS.length], fontSize: "1rem", flexShrink: 0 }}>✓</span>}
                </div>
              );
            })}
          </div>

          {/* Photo hint */}
          <p style={{
            textAlign: "center", fontSize: "0.78rem",
            color: "rgba(241,225,148,0.3)", marginBottom: 20,
            background: "rgba(241,225,148,0.04)", border: "1px solid rgba(241,225,148,0.1)",
            borderRadius: 10, padding: "8px 14px",
          }}>
            📷 انقر على الأيقونة لرفع شعار الفريق (اختياري)
          </p>

          {/* Start CTA */}
          <button
            data-testid="tournament-start-btn"
            onClick={handleStart}
            disabled={consuming}
            style={{
              width: "100%", padding: "16px 0", borderRadius: 16,
              background: consuming ? "rgba(91,14,20,0.5)" : "linear-gradient(135deg, #5B0E14, #9A1E28)",
              color: GOLD, border: "2px solid rgba(241,225,148,0.3)",
              fontFamily: "Cairo, sans-serif", fontWeight: 900, fontSize: "1.1rem",
              cursor: consuming ? "not-allowed" : "pointer",
              boxShadow: "0 4px 20px rgba(91,14,20,0.5)",
              opacity: consuming ? 0.7 : 1,
            }}
          >
            {consuming ? "⏳ جاري البدء..." : "إنشاء البطولة 🏆"}
          </button>
        </div>
      </div>
    </div>
  );
}
